// eslint-disable-next-line no-use-before-define
import React, { Fragment, useEffect, useRef, useState, useContext } from 'react';
import { styled } from '@mui/system';
import dayjs from 'op-utils/dayjs';
import { LoadingSpinner, Card } from 'shared-components/components';
import { getRemSize } from 'shared-components/StyledComponents/functions';
import { DocumentType } from './Summary/interfaces';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_PATIENT_DOCUMENT, APPROVE_DOCUMENT, SUBMIT_EDITABLE_AUTO_DOCS } from './PatientProfileQueries';
import useInterval from 'shared-components/utils/useInterval';
import { Box, Stack, Typography, useTheme, Button, Snackbar, Alert, IconButton, Chip } from '@mui/material';
import {
  FindInPageOutlined as FindInPageOutlinedIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { UserContextType } from '../../../contexts/UserContext/UserContext';
import UserContext from '../../../contexts/UserContext/UserContext';
import { TENANT_CONFIG_QUERY } from '../../../components/RO/Base/TenantConfigQuery';
import { Logger } from 'shared-components/utils';
import * as Sentry from '@sentry/browser';
import AutoDocEditor, { AutoDocEditorDocument } from '../Careplan/SubmissionPage/AutoDocEditor/AutoDocEditor';
import { normaliseDocument } from '../Documents/DocumentsList';
import { LoadingButton } from '@mui/lab';

export const CURRENT_USER_IS_PSO = gql`
  query currentUserIsPso {
    user {
      id
      isPso
    }
  }
`;

const logger = new Logger('ROPatientDocs');

interface ROPatientDocProps {
  document: DocumentType | null;
  isEditMode: boolean;
  showUnsubmittedChanges?: boolean;
  setEditMode?: (docId: string, isEditing: boolean) => void;
  onDocumentSubmitted?: () => void;
}

const StyledCol = styled(Card)`
  padding-left: 0;
  padding-right: 0;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const toAutoDocEditorDocument = (doc: DocumentType): AutoDocEditorDocument => ({
  editableDocumentId: doc.editableDocumentGroupId ?? '',
  name: doc.description || doc.title,
  previewUrl: doc.docUrl,
  viewed: true,
});

const ROPatientDocs = (props: ROPatientDocProps): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [document, setDocument] = useState<DocumentType | null>(null);
  const [fetchAttempts, setFetchAttempts] = useState(0);
  const lastProbeErrorRef = useRef<Record<string, unknown> | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [timeoutReached, setTimeoutReached] = useState(false);
  const showEditor = props.isEditMode && props.document?.status?.toLowerCase() !== 'void';
  const { setEditMode = () => {} } = props;
  const { state, hasFeature } = useContext<UserContextType>(UserContext);
  const { data: tenantConfigData } = useQuery(TENANT_CONFIG_QUERY);
  const { data: currentUserData } = useQuery(CURRENT_USER_IS_PSO);
  const isPso = Boolean(currentUserData?.user?.isPso);
  const documentApprovalEnabled = hasFeature('DocumentApproval');
  const fetchDelay = 3000;
  const maxFetchAttempts = 10;
  const headerDoc = document ?? props.document;
  const showUnsubmittedChip =
    props.document?.status?.toLowerCase() === 'approved' &&
    (props.showUnsubmittedChanges ?? props.document?.hasUncommittedChanges ?? false);
  const showPreviewMetadataRow = Boolean(headerDoc);
  const theme = useTheme();
  const [getDocument, { data }] = useLazyQuery(GET_PATIENT_DOCUMENT, {
    fetchPolicy: 'network-only',
  });
  const [approveDocument] = useMutation(APPROVE_DOCUMENT);
  const [submitEditableAutoDocs] = useMutation(SUBMIT_EDITABLE_AUTO_DOCS);

  type DocViewerErrorSource = 'fetch_probe' | 'iframe';
  type DocViewerErrorKind = 'http_final' | 'network_final' | 'max_attempts' | 'no_url_final' | 'iframe_error';

  const captureDocViewerError = (
    source: DocViewerErrorSource,
    kind: DocViewerErrorKind,
    extra: Record<string, unknown> = {},
  ): void => {
    const docId = document?.id ?? data?.document?.id ?? props.document?.id ?? null;
    const docHash = document?.hash ?? data?.document?.hash ?? null;

    const extra_sentry_info = {
      source,
      kind,
      documentId: docId,
      fetchAttempts,
      lastProbeError: lastProbeErrorRef.current,
      ...extra,
    };

    Sentry.captureMessage('RO document viewer load error', {
      level: 'error',
      extra: extra_sentry_info,
      fingerprint: ['ro-doc-viewer-error', source, kind, String(docId), String(docHash ?? 'no-hash')],
    });
  };

  const handleIframeError = (): void => {
    captureDocViewerError('iframe', 'iframe_error');
    setLoading(false);
    setTimeoutReached(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleApprove = async () => {
    if (!props.document?.id) return;
    if (props.document.status === 'Draft') return;

    try {
      const { data: mutationData } = await approveDocument({
        variables: {
          documentId: props.document.id,
          editableDocumentGroupId: props.document.editableDocumentGroupId ?? undefined,
        },
      });

      // Only update local document state if the mutation was successful
      if (mutationData?.approveDocument && document) {
        const { document: updatedDoc, docUrl } = mutationData.approveDocument;
        setDocument({
          ...document,
          ...(updatedDoc ?? {}),
          ...(docUrl ? { docUrl } : {}),
        });
      }

      // Show success toast notification
      showSnackbar('Document approved', 'success');
    } catch (error) {
      Sentry.captureMessage('Error approving document: ' + error, { level: 'error' });

      // Show error toast notification
      showSnackbar('Approval failed', 'error');
    }
  };

  const handleSubmitEdittedDocument = async () => {
    if (!props.document?.editableDocumentGroupId) return;
    try {
      await submitEditableAutoDocs({
        variables: {
          editableDocumentGroupId: props.document?.editableDocumentGroupId ?? '',
        },
      });
      showSnackbar('Document approved', 'success');
      props.onDocumentSubmitted?.();
    } catch (error) {
      setIsSubmitting(false);
      showSnackbar('Document approval failed', 'error');
    }
  };

  const handleRetry = () => {
    setFetchAttempts(0);
    setTimeoutReached(false);
    setLoading(true);
    fetchDocument();
  };

  const fetchDocument = () => {
    if (data && data.document?.docUrl) {
      fetch(data.document?.docUrl)
        .then((res) => {
          if (res.ok) {
            lastProbeErrorRef.current = null;
            if (document?.hash !== data.document?.hash) {
              setDocument(normaliseDocument(data.document));
            }
            setLoading(false);
            setFetchAttempts(0);
            setTimeoutReached(false);
          } else {
            lastProbeErrorRef.current = {
              type: 'http',
              status: res.status,
              statusText: res.statusText,
            };
            // Handle non-OK responses
            if (fetchAttempts >= maxFetchAttempts) {
              captureDocViewerError('fetch_probe', 'http_final', { status: res.status });
              setLoading(false);
              setTimeoutReached(true);
            }
          }
        })
        .catch((error) => {
          lastProbeErrorRef.current = {
            type: 'network',
            message: error instanceof Error ? error.message : String(error),
          };
          // Handle network errors
          if (fetchAttempts >= maxFetchAttempts) {
            captureDocViewerError('fetch_probe', 'network_final', {
              message: error instanceof Error ? error.message : String(error),
            });
            setLoading(false);
            setTimeoutReached(true);
          }
        })
        .finally(() => {
          // Always increment fetch attempts to prevent infinite loops
          setFetchAttempts((attempts) => {
            const newAttempts = attempts + 1;
            if (newAttempts >= maxFetchAttempts) {
              captureDocViewerError('fetch_probe', 'max_attempts');
              setLoading(false);
              setTimeoutReached(true);
            }
            return newAttempts;
          });
        });
    } else {
      lastProbeErrorRef.current = { type: 'no_url' };
      // If no document URL, stop loading and increment attempts
      setFetchAttempts((attempts) => {
        const newAttempts = attempts + 1;
        if (newAttempts >= maxFetchAttempts) {
          captureDocViewerError('fetch_probe', 'no_url_final');
          setLoading(false);
          setTimeoutReached(true);
        }
        return newAttempts;
      });
    }
  };

  useEffect(() => {
    setFetchAttempts(0);
    setTimeoutReached(false);
    setIsSubmitting(false);
    lastProbeErrorRef.current = null;

    if (!props.document?.id) {
      setDocument(null);
      setLoading(false);
      return;
    }

    // If the document is already displayed (same ID, not loading, URL resolved)
    // and only metadata changed (eg status), avoid re-triggering the loading state.
    const documentIsDisplayed = !loading && !!document?.docUrl && document?.id === props.document.id;
    if (documentIsDisplayed) {
      setDocument((prev) => (prev ? { ...prev, ...props.document } : props.document));
      return;
    }

    getDocument({
      variables: {
        documentId: props.document.id,
      },
    });
    setLoading(true);
    setDocument(props.document);
  }, [props.document]);

  // Fetch the document before useInterval starts the timer
  useEffect(() => {
    fetchDocument();
  }, [data]);

  useInterval(fetchDocument, fetchAttempts < maxFetchAttempts && loading ? fetchDelay : null);

  return (
    <>
      <Stack sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <StyledCol $noPadding>
          {!loading && !isSubmitting && !props.document && (
            <Stack alignItems="center" paddingTop={9}>
              <FindInPageOutlinedIcon fontSize="large" color="primary" />
              <Typography variant="body2" color="text.secondary">
                No document available
              </Typography>
            </Stack>
          )}
          {(loading || isSubmitting) && !timeoutReached && (
            <LoadingSpinner
              loadingText={'Loading Document Viewer'}
              subtitle={'This may take longer for larger documents'}
              relativeSpinner={true}
            />
          )}
          {!loading && !isSubmitting && timeoutReached && (
            <Stack alignItems="center" paddingTop={9} spacing={2}>
              <FindInPageOutlinedIcon fontSize="large" />
              <Typography variant="h6">Document Loading Timeout</Typography>
              <Typography variant="body1" textAlign="center" maxWidth={400}>
                The document is taking longer than expected to load. Please retry or contact support if the issue
                persists.
              </Typography>
              <Button variant="contained" color="primary" data-test-id="doc-timeout-retry" onClick={handleRetry}>
                Retry
              </Button>
            </Stack>
          )}
          {!loading && !isSubmitting && document && document.docUrl && (
            <Fragment>
              {showEditor && props.document?.editableDocumentGroupId ? (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <AutoDocEditor
                    key={props.document.id}
                    document={toAutoDocEditorDocument(document)}
                    updateDocumentStatus={() => {}}
                    initialEditMode={true}
                    onClose={() => {
                      showSnackbar('Changes discarded - reverted to the last version.', 'success');
                      props.document?.id && setEditMode?.(props.document.id, false);
                    }}
                    onApproveClick={() => setIsSubmitting(true)}
                    onApprove={async () => {
                      await handleSubmitEdittedDocument();
                      props.document?.id && setEditMode?.(props.document.id, false);
                    }}
                    showVersionNavigator={false}
                    approveText="Approve & submit"
                  />
                </Box>
              ) : (
                <>
                  <Stack padding={2} alignItems="center" justifyContent="space-between" direction="row">
                    <Stack>
                      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                        <Typography
                          variant="body1"
                          color={theme.palette.text.primary}
                          data-test-id="document-preview-header">
                          {(document || props.document)?.description || 'No title available'}
                        </Typography>
                        {showUnsubmittedChip && (
                          <Chip label="Unsubmitted changes" size="small" variant="outlined" color="warning" />
                        )}
                      </Stack>
                      {showPreviewMetadataRow && headerDoc && (
                        <Stack direction="row" gap={0.5}>
                          <Typography variant="body2" color={theme.palette.text.secondary}>
                            {headerDoc.status === 'Draft'
                              ? 'Draft'
                              : `${headerDoc.statusLabel ?? headerDoc.status} | ${
                                  CurrentAppConfig.UseDocumentTimezone
                                    ? dayjs(headerDoc.dateOfDocument).tz(state.timezone).format('LLLL')
                                    : dayjs(headerDoc.dateOfDocument).tz('utc').format('LLLL')
                                }`}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    <Stack direction="row" gap={1}>
                      {!isPso &&
                        props.document?.editableDocumentGroupId &&
                        (document.status?.toLowerCase() === 'approved' ||
                          document.status?.toLowerCase() === 'loading' ||
                          document.status?.toLowerCase() === 'draft') && (
                          <LoadingButton
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => props.document?.id && setEditMode?.(props.document.id, true)}
                            data-test-id="document-edit-button"
                            loading={document.status?.toLowerCase() === 'loading'}>
                            Edit
                          </LoadingButton>
                        )}
                      {/* Document Approval is enabled and the document is not approved */}
                      {!isPso &&
                        documentApprovalEnabled &&
                        (document || props.document)?.status &&
                        tenantConfigData?.tenantConfig?.documentApprovalStatuses?.includes(
                          (document || props.document)?.status,
                        ) &&
                        (document || props.document)?.manualApprovedAt == null && (
                          <Button variant="contained" color="primary" onClick={handleApprove}>
                            APPROVE
                          </Button>
                        )}
                    </Stack>
                  </Stack>
                  <iframe
                    title="patientPDFView"
                    src={`${document.docUrl}#view=fitH`}
                    width="100%"
                    style={{ height: 'calc(100% - 60px)' }}
                    onLoad={(): void => {
                      setLoading(false);
                    }}
                    onError={handleIframeError}>
                    This browser does not support PDFs.
                  </iframe>
                </>
              )}
            </Fragment>
          )}
        </StyledCol>
      </Stack>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          icon={false}
          sx={{
            width: '100%',
            backgroundColor: '#000000',
            color: '#ffffff',
            '& .MuiAlert-message': {
              color: '#ffffff',
            },
            '& .MuiIconButton-root': {
              color: '#ffffff',
            },
            '& .MuiIconButton-root:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          action={
            <IconButton aria-label="close" color="inherit" size="small" onClick={handleSnackbarClose}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ROPatientDocs;
